/* eslint-disable no-nested-ternary */
import {
  Box,
  Button,
  FormHelperText,
  Typography,
  useTheme,
} from '@mui/material';
import { Gauge, gaugeClasses } from '@mui/x-charts/Gauge';
import PageSection from '@product-framework/components/PageSection';
import {
  DATE_FORMAT,
  useLocalization,
  useUserAccess,
} from '@ui-framework/index';
import { FormattedMessage } from 'react-intl';
import ReadMoreText from '@ui-framework/components/UI/ReadMoreText';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import AiLoader from './AiLoader';
import AiIcon from './AiIcon';
import getOverallSentimentConfig from './lib/getOverallSentimentConfig';
import { useAiSentiment } from './AiSentimentProvider';
import { useServiceModule } from '../../providers/ServiceModuleProvider';

const REFRESH_AI_STATS_ENABLED = false;

function OverallRating({ value }) {
  const theme2 = useTheme();

  let color = 'primary';
  if (value > 6) {
    color = theme2.palette.success.main;
  } else if (value >= 4) {
    color = theme2.palette.warning.main;
  } else {
    color = theme2.palette.error.main;
  }

  return (
    <Box
      sx={{
        width: '100px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        marginTop: -1,
      }}
      textAlign="center">
      <Gauge
        value={value}
        startAngle={0}
        endAngle={360}
        innerRadius="85%"
        outerRadius="100%"
        valueMin={0}
        valueMax={10}
        width={60}
        height={60}
        sx={() => ({
          [`& .${gaugeClasses.valueText}`]: {
            fontSize: 20,
          },
          [`& .${gaugeClasses.valueArc}`]: {
            fill: color,
          },
        })}
      />
      <Typography
        sx={{ marginTop: -1 }}
        textAlign="center"
        fontWeight={500}
        variant="body2"
        color="tertiary.main">
        <FormattedMessage id="RATING" defaultMessage="Rating" />
      </Typography>
    </Box>
  );
}

function OverallSentiment({ config, dated }) {
  const { text, Icon, color } = config;

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1}>
        {Icon && <Icon color={color} />}
        <Typography variant="h6" color={`${color}.main`}>
          {text}
        </Typography>
      </Box>

      <FormHelperText>
        <FormattedMessage id="UPDATED" defaultMessage="Updated" />:{' '}
        {dated ? dayjs(dated).format(DATE_FORMAT.LONG_DATE) : '-'}
      </FormHelperText>
    </Box>
  );
}

function SentimentAnalysis({ serviceJob }) {
  const { formatMessage } = useLocalization();
  const { loadingAiAnalysis, aiAnalysis } = useAiSentiment();

  const userAccess = useUserAccess();
  const [aiData, setAiData] = useState({});
  const { moduleSettings } = useServiceModule();
  const display =
    (userAccess.isSA() || userAccess.isAdmin()) &&
    moduleSettings?.insightPurchased &&
    serviceJob?.serviceJobStatus !== 'INCOMPLETE';

  const initialize = async (jobId, aiAnalysisArg) => {
    if (aiAnalysisArg?.ai?.sentiment) {
      const { text, Icon, color } = getOverallSentimentConfig({
        type: aiAnalysisArg?.ai?.sentiment,
      });
      setAiData({
        ...aiAnalysisArg?.ai,
        sentimentConfig: { text, Icon, color },
      });
    }
  };

  useEffect(() => {
    if (display) {
      if (!loadingAiAnalysis) {
        initialize(serviceJob?.id, aiAnalysis);
      }
    }
  }, [serviceJob?.id, display, loadingAiAnalysis]);

  if (!display) {
    return null;
  }

  return (
    <Box>
      <PageSection
        headerIcon={AiIcon}
        sx={{ mt: -1, border: 'solid 1px #cdcdcdff' }}
        subSection
        label={formatMessage({
          id: 'SENTIMENT_ANALYSIS',
          defaultMessage: 'Sentiment Analysis',
        })}>
        {loadingAiAnalysis ? (
          <AiLoader>
            <FormattedMessage
              id="LOADING_SENTIMENT_ANALYSIS"
              defaultMessage="Loading Sentiment Analysis"
            />
            ...
          </AiLoader>
        ) : aiData?.sentiment ? (
          <Grid2 container spacing={2}>
            <Grid2 xs={2}>
              <OverallSentiment
                dated={aiData?.lastModifiedDateTime}
                config={aiData?.sentimentConfig}
              />
            </Grid2>
            <Grid2 xs={1}>
              <OverallRating
                value={aiData?.rating ? parseInt(aiData?.rating, 10) : '-'}
                config={aiData?.sentimentConfig}
              />
            </Grid2>
            <Grid2 xs={REFRESH_AI_STATS_ENABLED ? 8 : 9}>
              <ReadMoreText maxChars={370}>{aiData?.summary}</ReadMoreText>
            </Grid2>
            {REFRESH_AI_STATS_ENABLED && (
              <Grid2 xs textAlign="right">
                <Button
                  onClick={() => initialize(serviceJob?.id)}
                  sx={{ maxWidth: '120px', lineHeight: '1.3em' }}
                  variant="outlined">
                  <FormattedMessage
                    id="REFRESH_SENTIMENT"
                    defaultMessage="Refresh Sentiment"
                  />
                </Button>
              </Grid2>
            )}
          </Grid2>
        ) : (
          <Box sx={{ ml: 0.5, mt: -1 }}>
            <FormattedMessage
              id="NO_SENTIMENT_ANALYSIS_TO_DISPLAY"
              defaultMessage="No Sentiment Analysis to display"
            />
          </Box>
        )}
      </PageSection>
    </Box>
  );
}

export default SentimentAnalysis;
